import { doc, getDoc, getDocs, collection } from "firebase/firestore";
import { getFunctions, httpsCallable } from "firebase/functions";
import { db } from "./firebase";
import {
  IntegrationDoc, IntegrationId,
  AiIntegrationConfig, GwsIntegrationConfig, SlackIntegrationConfig,
  EmailIntegrationConfig, ManualIntegrationConfig,
  M365IntegrationConfig, TeamsIntegrationConfig,
} from "@/types";

// ─── AI info-gathering round limit ────────────────────────────────
//
// How many times the AI may ask a submitter for more information before
// escalating the ticket to a human agent. Mirrors the bounds enforced in
// helpdesk-functions/functions/src/lib/aiProvider.ts — the server clamps
// on read, so these values are for UI affordance only.

export const DEFAULT_MAX_INFO_ROUNDS = 3;
export const MIN_MAX_INFO_ROUNDS = 1;
export const MAX_MAX_INFO_ROUNDS = 10;

// Descriptor catalog — every integration the app knows about.
// Adding a new provider: add a row here + (if needed) implement
// the corresponding Cloud Function and update SECRET_REGISTRY in
// helpdesk-functions/functions/src/secretsManagement.ts.

export const INTEGRATION_DEFAULTS: Record<IntegrationId, IntegrationDoc> = {
  manual: {
    id: "manual", type: "identity",
    displayName: "Manual Sign-in",
    description: "Email + password authentication. Always available.",
    enabled: true, required: true,
    config: { allowSelfSignup: false, passwordMinLength: 8 } as ManualIntegrationConfig,
    requiredSecrets: [],
  },
  gws: {
    id: "gws", type: "identity",
    displayName: "Google Workspace",
    description: "Sync users from Google Workspace and enable Google SSO.",
    enabled: false, required: false,
    config: {
      customerDomain: "", customerId: "", serviceAccountEmail: "",
      targetGroup: "", syncTime: "02:00",
      autoDisableSuspended: true, autoDeleteRemoved: false,
      syncOrgUnits: true, notifyOnErrors: true,
      allowedDomain: "",
    } as GwsIntegrationConfig,
    requiredSecrets: ["GWS_SERVICE_ACCOUNT_KEY"],
  },
  m365: {
    id: "m365", type: "identity",
    displayName: "Microsoft 365",
    description: "Sync users from Microsoft 365. (Coming soon — config UI only.)",
    enabled: false, required: false,
    config: {} as M365IntegrationConfig,
    requiredSecrets: ["M365_CLIENT_SECRET"],
  },
  slack: {
    id: "slack", type: "notification",
    displayName: "Slack",
    description: "Post ticket alerts and digests to Slack channels.",
    enabled: false, required: false,
    config: {
      channels: {
        criticalTickets: "#it-critical", allTickets: "#it-helpdesk",
        slaAlerts: "#it-alerts", syncErrors: "#it-ops", dailyDigest: "#it-digest",
      },
      triggers: {
        newTicket: true, p1Escalation: true, slaWarning: true,
        ticketResolved: false, dailyDigest: true, syncErrors: true,
      },
    } as SlackIntegrationConfig,
    requiredSecrets: ["SLACK_BOT_TOKEN"],
  },
  teams: {
    id: "teams", type: "notification",
    displayName: "Microsoft Teams",
    description: "Post ticket alerts to Teams. (Coming soon — config UI only.)",
    enabled: false, required: false,
    config: {} as TeamsIntegrationConfig,
    requiredSecrets: ["TEAMS_WEBHOOK_URL"],
  },
  email: {
    id: "email", type: "notification",
    displayName: "Email Notifications",
    description: "Outgoing email via Resend, SendGrid, or your own SMTP.",
    enabled: true, required: false,
    config: {
      provider: "resend",
      fromAddress: "", replyTo: "",
      ticketConfirmation: true, agentAssignment: true, csatSurvey: true,
    } as EmailIntegrationConfig,
    requiredSecrets: ["RESEND_API_KEY"],
  },
  ai: {
    id: "ai", type: "ai",
    displayName: "AI Triage & Chat",
    description: "AI provider for ticket triage and the assistant.",
    enabled: true, required: true,
    config: {
      provider: "gemini",
      triageModel: "gemini-2.0-flash",
      autoAssignThreshold: 80,
      sendSummaryToUser: true,
      selfServiceDeflection: true,
      maxInfoRounds: DEFAULT_MAX_INFO_ROUNDS,
    } as AiIntegrationConfig,
    requiredSecrets: ["GEMINI_API_KEY"],
  },
};

export async function getIntegration<T = unknown>(id: IntegrationId): Promise<IntegrationDoc<T>> {
  const ref = doc(db, "integrations", id);
  const snap = await getDoc(ref);
  const defaults = INTEGRATION_DEFAULTS[id] as IntegrationDoc<T>;
  if (!snap.exists()) return defaults;
  const data = snap.data() as Partial<IntegrationDoc<T>>;
  return {
    ...defaults,
    ...data,
    config: { ...(defaults.config as object), ...(data.config as object) } as T,
  };
}

export async function getAllIntegrations(): Promise<IntegrationDoc[]> {
  const snap = await getDocs(collection(db, "integrations"));
  const stored = new Map(snap.docs.map((d) => [d.id, d.data() as Partial<IntegrationDoc>]));
  return (Object.keys(INTEGRATION_DEFAULTS) as IntegrationId[]).map((id) => {
    const defaults = INTEGRATION_DEFAULTS[id];
    const data = stored.get(id);
    if (!data) return defaults;
    return {
      ...defaults,
      ...data,
      config: { ...(defaults.config as object), ...(data.config as object) },
    };
  });
}

// Writes go through an admin-only Cloud Function rather than straight to
// Firestore: the callable validates the payload and records an auditLog
// entry naming who changed what. Firestore rules deny client writes to
// `integrations/*`, so this is the only path.
export async function saveIntegration<T>(id: IntegrationId, patch: Partial<IntegrationDoc<T>>): Promise<void> {
  const fn = httpsCallable(getFunctions(), "saveIntegrationSettings");
  await fn({
    id,
    ...(patch.enabled !== undefined ? { enabled: patch.enabled } : {}),
    ...(patch.config !== undefined ? { config: patch.config } : {}),
  });
}

export async function setIntegrationEnabled(id: IntegrationId, enabled: boolean): Promise<void> {
  await saveIntegration(id, { enabled });
}

export async function saveIntegrationConfig<T>(id: IntegrationId, config: T): Promise<void> {
  await saveIntegration<T>(id, { config });
}
